import React from 'react';
import { Send, Bot, Sparkles, Zap, Shield, Cloud, TrendingUp, Clock, Trash2, Copy, ArrowLeft, User } from 'lucide-react';
import { academyLogger } from "utils/logger";

const AIPage = ({ onClose }) => {
  const [message, setMessage] = React.useState('');
  const [conversation, setConversation] = React.useState([
    {
      id: 1,
      type: 'ai',
      content: 'Hello! I\'m your AI assistant. I can help you with domain names, website setup, SEO optimization, and much more. What would you like to know today?',
      timestamp: new Date()
    }
  ]);
  const [isTyping, setIsTyping] = React.useState(false);

  const quickPrompts = [
    {
      title: "Domain Suggestions",
      prompt: "Suggest the best domain name for my business",
      icon: Cloud,
    },
    {
      title: "Registration Help",
      prompt: "Explain how domain registration works",
      icon: Shield,
    },
    {
      title: "Website Setup",
      prompt: "Help me set up my website on a new domain",
      icon: Zap,
    },
    {
      title: "SEO Tips",
      prompt: "Give me SEO tips to boost my domain ranking",
      icon: TrendingUp,
    }
  ];

  const handleSendMessage = async () => {
    if (message.trim() === '') return;

    const userMessage = {
      id: conversation.length + 1,
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setConversation(prev => [...prev, userMessage]);
    setMessage('');
    setIsTyping(true);

    const apiMessages = [
      {
        role: "user",
        content: message
      }
    ];

    try {
      // Import api dynamically if needed
      const { api } = await import('../api/action/ai');
      const response = await api.streamChatCompletion(
        apiMessages,
        "llama-3.3-70b-versatile",
        0.7,
        20
      );

      if (response.choices && response.choices[0] && response.choices[0].message) {
        const aiResponse = {
          id: conversation.length + 2,
          type: 'ai',
          content: response.choices[0].message.content,
          timestamp: new Date()
        };
        setConversation(prev => [...prev, aiResponse]);
      }
    } catch (error) {
      academyLogger.error('API error:', error);
      const errorMessage = {
        id: conversation.length + 2,
        type: 'ai',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setConversation(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickPrompt = (prompt) => {
    setMessage(prompt);
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearConversation = () => {
    setConversation([{
      id: 1,
      type: 'ai',
      content: 'Hello! I\'m your AI assistant. I can help you with domain names, website setup, SEO optimization, and much more. What would you like to know today?',
      timestamp: new Date()
    }]);
  };

  const copyLastResponse = () => {
    const lastAIResponse = [...conversation].reverse().find(msg => msg.type === 'ai');
    if (lastAIResponse) {
      navigator.clipboard.writeText(lastAIResponse.content);
    }
  };

  // Auto-resize textarea
  const textareaRef = React.useRef(null);

  React.useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  }, [message]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pt-20">
      <div className="max-w-7xl mx-auto h-screen px-4 py-4">
        <div className="h-full flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden">
          
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 border-b border-indigo-500">
            <div className="flex items-center gap-4">
              <button
                onClick={onClose}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 hover:scale-105"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-lg">
                  <Bot className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white">AI Assistant</h1>
                  <p className="text-xs text-indigo-100">Always here to help</p>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={clearConversation}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 hover:scale-105"
                title="Clear conversation"
              >
                <Trash2 className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={copyLastResponse}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 hover:scale-105"
                title="Copy last response"
              >
                <Copy className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar - Desktop only */}
            <div className="hidden lg:flex lg:w-80 flex-col border-r border-slate-200 bg-slate-50">
              <div className="p-6 flex-1 overflow-y-auto">
                <div className="flex items-center gap-2 mb-6">
                  <Sparkles className="w-5 h-5 text-slate-700" />
                  <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Quick Actions</h2>
                </div>

                <div className="space-y-3 mb-8">
                  {quickPrompts.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={index}
                        onClick={() => handleQuickPrompt(item.prompt)}
                        className="w-full text-left p-4 rounded-xl bg-white border border-slate-200 hover:border-slate-400 hover:shadow-md transition-all duration-300 group"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-slate-100 group-hover:bg-slate-200 transition-colors">
                            <Icon className="w-4 h-4 text-slate-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm text-slate-800 mb-1">{item.title}</h3>
                            <p className="text-xs text-slate-500 line-clamp-2">{item.prompt}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="pt-6 border-t border-slate-200">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Features</h3>
                  <div className="space-y-3">
                    {[
                      { icon: "⚡", title: "Instant Responses", desc: "Get answers immediately" },
                      { icon: "🎯", title: "Expert Advice", desc: "Professional guidance" },
                      { icon: "🔒", title: "Secure & Private", desc: "Your data is safe" },
                      { icon: "🌟", title: "24/7 Available", desc: "Always ready to help" }
                    ].map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white transition-colors">
                        <span className="text-xl">{feature.icon}</span>
                        <div>
                          <p className="text-sm font-medium text-slate-700">{feature.title}</p>
                          <p className="text-xs text-slate-500">{feature.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-slate-200 bg-white">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                  <Clock className="w-4 h-4 text-slate-600" />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      {conversation.filter(msg => msg.type === 'user').length} messages
                    </p>
                    <p className="text-xs text-slate-500">This conversation</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 flex flex-col">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {conversation.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex gap-3 ${msg.type === 'user' ? 'flex-row-reverse' : 'flex-row'} animate-fade-in`}
                  >
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      msg.type === 'ai' 
                        ? 'bg-gradient-to-br from-indigo-500 to-purple-500' 
                        : 'bg-gradient-to-br from-indigo-600 to-indigo-700'
                    }`}>
                      {msg.type === 'ai' ? (
                        <Bot className="w-5 h-5 text-white" />
                      ) : (
                        <User className="w-5 h-5 text-white" />
                      )}
                    </div>
                    
                    <div className={`flex-1 max-w-2xl ${msg.type === 'user' ? 'flex justify-end' : ''}`}>
                      <div className={`p-4 rounded-2xl ${
                        msg.type === 'user'
                          ? 'bg-indigo-600 text-white rounded-tr-sm'
                          : 'bg-slate-100 text-slate-800 rounded-tl-sm'
                      } shadow-sm hover:shadow-md transition-shadow duration-200`}>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-xs mt-2 ${
                          msg.type === 'user' ? 'text-indigo-100' : 'text-slate-500'
                        }`}>
                          {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Typing Indicator */}
                {isTyping && (
                  <div className="flex gap-3 animate-fade-in">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div className="p-4 rounded-2xl rounded-tl-sm bg-slate-100">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          {[0, 1, 2].map((i) => (
                            <div
                              key={i}
                              className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"
                              style={{ animationDelay: `${i * 0.15}s` }}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-slate-500 ml-2">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="border-t border-slate-200 bg-white p-4">
                {/* Quick Prompts - Mobile */}
                <div className="lg:hidden flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
                  {quickPrompts.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={index}
                        onClick={() => handleQuickPrompt(item.prompt)}
                        className="flex-shrink-0 px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-sm font-medium text-slate-700 transition-colors flex items-center gap-2"
                      >
                        <Icon className="w-4 h-4" />
                        {item.title}
                      </button>
                    );
                  })}
                </div>

                <div className="flex gap-3 items-end">
                  <div className="flex-1 relative">
                    <textarea
                      ref={textareaRef}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask me anything about domains, hosting, or websites..."
                      disabled={isTyping}
                      rows={1}
                      className="w-full px-4 py-3 pr-12 rounded-xl bg-slate-50 border border-slate-200 focus:border-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-200 resize-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      style={{ minHeight: '48px', maxHeight: '120px' }}
                    />
                  </div>
                  <button
                    onClick={handleSendMessage}
                    disabled={message.trim() === '' || isTyping}
                    className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-slate-300 disabled:to-slate-400 text-white flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95 disabled:scale-100 disabled:cursor-not-allowed shadow-lg hover:shadow-xl disabled:shadow-none"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default AIPage;
